/**
 * DrawerFooter — sticky footer for the workflow detail drawer, shared
 * between ora's WorkflowDetailDrawer and the Outlook plugin's
 * InstanceDetailDrawer (plugin adoption lands in PR-10).
 *
 * Renders two rows:
 *   1. Copy-id row: Copy instance id (when instanceId is supplied — comes
 *      first so triage starts from the workflow's own id, which uniquely
 *      identifies the run across all logs / DB rows); Copy correlation id
 *      (always — falls back to instance id when no error correlation id
 *      is available); Copy session id + Kill session (when sessionId is
 *      supplied).
 *   2. Action row: Cancel Workflow (when canCancel) + extraActions slot
 *      (plugin's Retry button goes here) + Close.
 *
 * The Cancel button manages its own ``confirmingCancel`` dialog state so the
 * shell doesn't need to track it. Kill-session loading state is also local.
 *
 * ``density="compact"`` (used by the plugin's bottom-anchored drawer)
 * tightens vertical padding to conserve screen space.
 */

import { useState, type ReactNode } from "react";
import { CircularProgress } from "@mui/material";
import { DebugHint } from "./DebugActionsPanel";

export interface DrawerFooterProps {
  /** Workflow instance id — surfaced as the first copy link so triage starts
   * from the durable id stored in `workflow_instances`, the DT scheduler
   * history, MLflow traces, and the SignalR group name. When absent the
   * link is hidden and Copy correlation id remains the entry point. */
  instanceId?: string | null;
  onCopyInstance?: () => void;
  onCopyCorrelation: () => void;
  sessionId?: string | null;
  onCopySession?: () => void;
  onKillSession?: () => Promise<void> | void;
  /** Whether the Cancel Workflow button is shown (status === active/hitl). */
  canCancel?: boolean;
  /** Whether Cancel + the kill button are temporarily disabled (e.g. an action is in flight). */
  isActionInFlight?: boolean;
  onCancel?: () => Promise<void> | void;
  onClose: () => void;
  /** Extra action(s) shown beside Cancel — plugin uses this for Retry. */
  extraActions?: ReactNode;
  density?: "comfortable" | "compact";
  /** When false, the copy-id row is omitted (running-drawer puts those
   *  links in the More information pane). Default true. */
  showCopyRow?: boolean;
  /** running-drawer: shows the "More information" entry-point button in the
   *  action row (pinned here, alongside Cancel/Close, instead of inline in
   *  the scrolling body) when supplied. Whether the pane is currently open
   *  is owned by the caller — this button only ever opens it; the pane's own
   *  back button closes it. */
  onOpenMoreInformation?: () => void;
  /** Whether the more-information pane is currently open — exposed as the
   *  button's `aria-expanded` so assistive tech gets the real state even
   *  though this button only ever opens (never toggles) it. */
  moreInformationOpen?: boolean;
  /** When false, the action row has no Close button. The Outlook pane passes
   *  false: its drawer has ONE way back (the top back row + Esc) — decided
   *  2026-10-06. Default true: ora's drawer keeps its footer Close. */
  showClose?: boolean;
}

export function DrawerFooter({
  instanceId,
  onCopyInstance,
  onCopyCorrelation,
  sessionId,
  onCopySession,
  onKillSession,
  canCancel = false,
  isActionInFlight = false,
  onCancel,
  onClose,
  extraActions,
  density = "comfortable",
  showCopyRow = true,
  onOpenMoreInformation,
  moreInformationOpen = false,
  showClose = true,
}: DrawerFooterProps) {
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [killingSession, setKillingSession] = useState(false);

  const idRowPadding = density === "compact" ? "px-4 pt-1" : "px-6 pt-2";
  const actionRowPadding = density === "compact" ? "px-4 py-2" : "px-6 py-4";
  // Compact (plugin) action-row buttons: small px-3/py-1/text-xs chrome
  // instead of ora's roomier h-10/px-4/text-sm.
  const isCompact = density === "compact";
  const moreInfoButtonClass = isCompact
    ? "px-3 py-1 flex items-center gap-1.5 rounded border border-primary-gray-light-300 text-primary-gray-light-700 text-xs font-medium hover:bg-primary-gray-light-50"
    : "h-10 px-4 flex items-center gap-1.5 rounded-lg border border-primary-gray-light-300 text-primary-gray-light-700 text-sm font-medium hover:bg-primary-gray-light-50";
  const cancelButtonClass = isCompact
    ? "px-3 py-1 flex justify-center items-center rounded border border-primary-error-300 text-primary-error-700 text-xs font-semibold hover:bg-primary-error-50 disabled:opacity-40 disabled:cursor-not-allowed"
    : "h-10 px-4 flex justify-center items-center rounded-lg border border-primary-error-300 text-primary-error-700 text-sm font-semibold hover:bg-primary-error-50 disabled:opacity-40 disabled:cursor-not-allowed";
  const cancelConfirmButtonClass = isCompact
    ? "px-3 py-1 flex justify-center items-center rounded border border-primary-error-300 text-primary-error-700 text-xs font-semibold hover:bg-primary-error-50 disabled:opacity-40 disabled:cursor-not-allowed"
    : "h-10 px-4 flex justify-center items-center rounded-lg border border-primary-error-300 text-primary-error-700 text-sm font-semibold hover:bg-primary-error-50 disabled:opacity-40 disabled:cursor-not-allowed";
  const cancelPromptTextClass = isCompact
    ? "text-xs text-primary-gray-light-700"
    : "text-sm text-primary-gray-light-700";
  const closeButtonClass = isCompact
    ? "px-3 py-1 flex justify-center items-center btn-gray-light-700 rounded border-[1px] border-[solid] border-primary-gray-light-300"
    : "w-[4.25rem] h-10 flex justify-center items-center btn-gray-light-700 rounded-lg border-[1px] border-[solid] border-primary-gray-light-300";
  const closeTextClass = isCompact
    ? "text-xs text-primary-gray-light-700 [word-break:keep-all] whitespace-nowrap inline"
    : "text-sm text-primary-gray-light-700 [word-break:keep-all] whitespace-nowrap inline";
  // gap-3 (0.75rem) reads as normal spacing next to comfortable's h-10
  // buttons but as loose padding between compact's much shorter px-3/py-1
  // buttons — tighten it to keep the same visual proportion.
  const actionRowGap = isCompact ? "gap-2" : "gap-3";

  const handleKillSession = async () => {
    if (!onKillSession) return;
    setKillingSession(true);
    try {
      await onKillSession();
    } finally {
      setKillingSession(false);
    }
  };

  return (
    <>
      {showCopyRow ? (
      <div className={`${idRowPadding} flex items-center gap-3 text-2xs`}>
        {instanceId && onCopyInstance && (
          <DebugHint id="copy-instance" hint="Workflow instance id — the durable id stored in workflow_instances, DT history, MLflow traces, and the SignalR group name">
            <button
              type="button"
              onClick={onCopyInstance}
              className="text-primary-gray-light-600 hover:text-primary-gray-light-900 underline"
              data-testid="copy-instance"
            >
              Copy instance id
            </button>
          </DebugHint>
        )}
        <DebugHint id="copy-correlation" hint="Correlation id — paste into product API / substrate log search">
          <button
            type="button"
            onClick={onCopyCorrelation}
            className="text-primary-gray-light-600 hover:text-primary-gray-light-900 underline"
            data-testid="copy-correlation"
          >
            Copy correlation id
          </button>
        </DebugHint>
        {sessionId && (
          <>
            {onCopySession && (
              <DebugHint id="copy-session" hint="Exec-plane RPA session token — paste into log search for browser-side triage">
                <button
                  type="button"
                  onClick={onCopySession}
                  className="text-primary-gray-light-600 hover:text-primary-gray-light-900 underline"
                  data-testid="copy-session"
                >
                  Copy session id
                </button>
              </DebugHint>
            )}
            {onKillSession && (
              <DebugHint id="kill-session" hint="Debug only — DELETE /sessions/{token} against the exec-plane to free a stuck browser session">
                <button
                  type="button"
                  onClick={() => void handleKillSession()}
                  disabled={killingSession}
                  className="text-primary-error-700 hover:text-primary-error-900 underline disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="kill-session"
                >
                  {killingSession ? "Killing…" : "Kill session"}
                </button>
              </DebugHint>
            )}
          </>
        )}
      </div>
      ) : null}
      {/* Action row */}
      <div className={`${actionRowPadding} border-t border-primary-gray-light-100 flex justify-between items-center ${actionRowGap}`}>
        <div className={`flex-1 flex items-center ${actionRowGap}`}>
          {onOpenMoreInformation && (
            <button
              type="button"
              onClick={onOpenMoreInformation}
              aria-expanded={moreInformationOpen}
              className={moreInfoButtonClass}
              data-testid="more-information-open"
            >
              <span aria-hidden="true" className="text-primary-gray-light-400">
                ›
              </span>
              More information
            </button>
          )}
          {canCancel && onCancel && (
            !confirmingCancel ? (
              <button
                type="button"
                onClick={() => setConfirmingCancel(true)}
                disabled={isActionInFlight}
                className={cancelButtonClass}
                data-testid="cancel-button"
              >
                Cancel Workflow
              </button>
            ) : (
              <div
                className={`flex items-center ${actionRowGap}`}
                role="dialog"
                aria-label="Confirm cancel"
                data-testid="cancel-confirm"
              >
                <span className={cancelPromptTextClass}>Cancel this workflow?</span>
                <button
                  type="button"
                  onClick={() => setConfirmingCancel(false)}
                  className={isCompact
                    ? "text-xs text-primary-gray-light-600 hover:text-primary-gray-light-900 underline"
                    : "text-sm text-primary-gray-light-600 hover:text-primary-gray-light-900 underline"}
                >
                  Keep running
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmingCancel(false);
                    void onCancel();
                  }}
                  disabled={isActionInFlight}
                  className={cancelConfirmButtonClass}
                  data-testid="cancel-confirm-button"
                >
                  {isActionInFlight ? <CircularProgress size={14} color="inherit" /> : "Yes, cancel"}
                </button>
              </div>
            )
          )}
          {extraActions}
        </div>
        {showClose && (
          <button
            onClick={onClose}
            className={closeButtonClass}
            data-testid="drawer-close-footer"
          >
            <span className={closeTextClass}>
              Close
            </span>
          </button>
        )}
      </div>
    </>
  );
}

export default DrawerFooter;
